"use client"

import { useState } from "react"
import { zodResolver } from "@hookform/resolvers/zod"
import * as z from "zod"
import { useForm } from "react-hook-form"
import { Button } from "@/components/ui/button"
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Input } from "@/components/ui/input"
import { PenIcon, Loader2, User, Mail, Phone, Building, MapPin, Globe, AlertCircle } from "lucide-react"
import CustomersService from "@/api/customers.service"
import { useToast } from "@/hooks/use-toast"
import countries from "@/components/Countries"
import PhoneInput from 'react-phone-number-input'
import 'react-phone-number-input/style.css'
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Separator } from "@/components/ui/separator"

const formSchema = z.object({
  firstName: z.string().min(1, "First name is required"),
  lastName: z.string().min(1, "Last name is required"),
  emailAddress: z.string().email("Please enter a valid email").or(z.string().length(0)),
  phoneNumber: z.string().optional(),
  street: z.string().optional(),
  city: z.string().optional(),
  state: z.string().optional(),
  zipCode: z.string().optional(),
  country: z.string().optional(),
  businessName: z.string().optional(),
})

interface EditCustomerFormProps {
  customer: any;
}

export function EditCustomerForm({ customer }: EditCustomerFormProps) {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Define your form
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: customer,
  })

  // Define a submit handler
  async function onSubmit(values: z.infer<typeof formSchema>) {
    setIsSubmitting(true);
    try {
      const response = await CustomersService.updateSpecificCustomer(customer.id, values);
      const result = response.data;
      if (result.result === "success") {
        toast({
          title: "Customer updated",
          description: "The customer information has been successfully updated.",
        });
        setTimeout(() => {
          location.reload();
        }, 2000);
      } else {
        toast({
          title: "Update failed",
          description: result.message || "Failed to update customer information",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error(error);
      toast({
        title: "Error occurred",
        description: "An unexpected error occurred while updating the customer",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div className="w-full max-w-4xl mx-auto px-4 sm:px-6 py-6">
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          <Card className="border-slate-200/60 shadow-sm overflow-hidden">
            <CardHeader className="pb-4 px-4 sm:px-6">
              <CardTitle className="text-lg font-medium flex items-center gap-2">
                <User size={18} className="text-primary" />
                Personal Information
              </CardTitle>
            </CardHeader>
            <Separator />
            <CardContent className="pt-5 px-4 sm:px-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                <FormField
                  control={form.control}
                  name="firstName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-sm font-medium">First Name <span className="text-red-500">*</span></FormLabel>
                      <FormControl>
                        <Input 
                          placeholder="First Name" 
                          {...field} 
                          className="h-11 focus-visible:ring-primary"
                        />
                      </FormControl>
                      <FormMessage className="text-xs" />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="lastName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-sm font-medium">Last Name <span className="text-red-500">*</span></FormLabel>
                      <FormControl>
                        <Input 
                          placeholder="Last Name" 
                          {...field} 
                          className="h-11 focus-visible:ring-primary"
                        />
                      </FormControl>
                      <FormMessage className="text-xs" />
                    </FormItem>
                  )}
                />
              </div>
            </CardContent>
          </Card>

          <Card className="border-slate-200/60 shadow-sm overflow-hidden">
            <CardHeader className="pb-4 px-4 sm:px-6">
              <CardTitle className="text-lg font-medium flex items-center gap-2">
                <Mail size={18} className="text-primary" />
                Contact Information
              </CardTitle>
            </CardHeader>
            <Separator />
            <CardContent className="pt-5 px-4 sm:px-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                <FormField
                  control={form.control}
                  name="emailAddress"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-sm font-medium">Email Address</FormLabel>
                      <FormControl>
                        <div className="relative">
                          <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                          <Input 
                            placeholder="customer@example.com" 
                            type="email"
                            {...field} 
                            className="h-11 pl-10 focus-visible:ring-primary"
                          />
                        </div>
                      </FormControl>
                      <FormMessage className="text-xs" />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="phoneNumber"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-sm font-medium">Phone Number</FormLabel>
                      <FormControl>
                        <div className="relative">
                          <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground z-10" />
                          <PhoneInput 
                            placeholder="+1 (555) 000-0000" 
                            {...field} 
                            className="h-11 pl-10 focus-visible:ring-primary w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50" 
                          />
                        </div>
                      </FormControl>
                      <FormMessage className="text-xs" />
                    </FormItem>
                  )}
                />
              </div>
            </CardContent>
          </Card>

          <Card className="border-slate-200/60 shadow-sm overflow-hidden">
            <CardHeader className="pb-4 px-4 sm:px-6">
              <CardTitle className="text-lg font-medium flex items-center gap-2">
                <MapPin size={18} className="text-primary" />
                Address Information
              </CardTitle>
            </CardHeader>
            <Separator />
            <CardContent className="pt-5 px-4 sm:px-6">
              <div className="grid grid-cols-1 gap-4 sm:gap-6">
                <FormField
                  control={form.control}
                  name="street"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-sm font-medium">Street Address</FormLabel>
                      <FormControl>
                        <Input 
                          placeholder="123 Main St" 
                          {...field} 
                          className="h-11 focus-visible:ring-primary"
                        />
                      </FormControl>
                      <FormMessage className="text-xs" />
                    </FormItem>
                  )}
                />

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                  <FormField
                    control={form.control}
                    name="city"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-sm font-medium">City</FormLabel>
                        <FormControl>
                          <Input 
                            placeholder="City" 
                            {...field} 
                            className="h-11 focus-visible:ring-primary"
                          />
                        </FormControl>
                        <FormMessage className="text-xs" />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="state"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-sm font-medium">State/Province</FormLabel>
                        <FormControl>
                          <Input 
                            placeholder="State" 
                            {...field} 
                            className="h-11 focus-visible:ring-primary"
                          />
                        </FormControl>
                        <FormMessage className="text-xs" />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="zipCode"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-sm font-medium">Zip/Postal Code</FormLabel>
                        <FormControl>
                          <Input 
                            placeholder="Zip Code" 
                            {...field} 
                            className="h-11 focus-visible:ring-primary"
                          />
                        </FormControl>
                        <FormMessage className="text-xs" />
                      </FormItem>
                    )}
                  />
                </div>

                <FormField
                  control={form.control}
                  name="country"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-sm font-medium">Country</FormLabel>
                      <FormControl>
                        <Select onValueChange={field.onChange} defaultValue={field.value}>
                          <SelectTrigger className="h-11 focus-visible:ring-primary">
                            <div className="flex items-center">
                              <Globe className="h-4 w-4 mr-2 text-muted-foreground" />
                              <SelectValue placeholder="Select country" />
                            </div>
                          </SelectTrigger>
                          <SelectContent className="max-h-[200px]">
                            {countries.map((country) => (
                              <SelectItem key={country} value={country}>
                                {country}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </FormControl>
                      <FormMessage className="text-xs" />
                    </FormItem>
                  )}
                />
              </div>
            </CardContent>
          </Card>

          <Card className="border-slate-200/60 shadow-sm overflow-hidden">
            <CardHeader className="pb-4 px-4 sm:px-6">
              <CardTitle className="text-lg font-medium flex items-center gap-2">
                <Building size={18} className="text-primary" />
                Business Information
              </CardTitle>
            </CardHeader>
            <Separator />
            <CardContent className="pt-5 px-4 sm:px-6">
              <div className="grid grid-cols-1 gap-4 sm:gap-6">
                <FormField
                  control={form.control}
                  name="businessName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-sm font-medium">Business Name</FormLabel>
                      <FormControl>
                        <div className="relative">
                          <Building className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                          <Input 
                            placeholder="Business Name" 
                            {...field} 
                            className="h-11 pl-10 focus-visible:ring-primary"
                          />
                        </div>
                      </FormControl>
                      <FormDescription className="text-xs text-muted-foreground">
                        Enter the business name if this customer represents a company
                      </FormDescription>
                      <FormMessage className="text-xs" />
                    </FormItem>
                  )}
                />
              </div>
            </CardContent>
          </Card>

          <div className="flex flex-col sm:flex-row justify-between gap-4 sm:gap-6 pt-2">
            <Button 
              type="button" 
              variant="outline"
              onClick={() => window.history.back()}
              className="h-11 px-6 order-2 sm:order-1"
            >
              Cancel
            </Button>
            <Button 
              type="submit" 
              className="bg-gradient-to-r from-primary to-primary/90 text-white shadow-sm hover:shadow-md transition-all h-11 px-6 order-1 sm:order-2"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <div className="flex items-center">
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" /> 
                  <span>Updating...</span>
                </div>
              ) : (
                <div className="flex items-center">
                  <PenIcon className="w-4 h-4 mr-2" /> 
                  <span>Save Changes</span>
                </div>
              )}
            </Button>
          </div>
          
          {Object.keys(form.formState.errors).length > 0 && (
            <div className="p-3 rounded-md bg-red-50 border border-red-100 flex items-start gap-3 mt-2">
              <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-red-800">Please fix the following errors:</p>
                <ul className="mt-1 text-xs text-red-700 list-disc list-inside">
                  {Object.entries(form.formState.errors).map(([field, error]) => (
                    <li key={field}>
                      {field.charAt(0).toUpperCase() + field.slice(1).replace(/([A-Z])/g, ' $1').toLowerCase()}: {error?.message as string}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}
        </form>
      </Form>
    </div>
  )
}
